import {isDensityUnit, isMassUnit, isMolarityUnit, isVolumeUnit} from 'inventory/InventoryUnitUtil';
import {StoichiometryVariable} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometryEquations';
import StoichiometrySolver, {
  StoichiometryCalculationVariableValues,
} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometrySolver';
import {
  getStandardChemistryUnitConversionFactorFromSymbol,
  getUnitFromTypedValue,
  DEFAULT_MASS_UNIT_SYMBOL,
  DEFAULT_MOL_UNIT_SYMBOL,
  DEFAULT_VOLUME_UNIT_SYMBOL,
  DEFAULT_DENSITY_UNIT_SYMBOL,
  DEFAULT_MOLARITY_UNIT_SYMBOL,
} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometryUnits';
import {
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {StructuredTableColumnGroups} from 'notebook/structured-tables/StructuredTableConstants';
import {CustomEval, CustomEvalResult, TypedValue} from 'notebook/tables/TableConstants';
import {
  coerceToPrimitiveNumber,
  coerceToPrimitiveBoolean,
  isTypedValueEmpty,
  createFloatTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {ResolveQueryFn} from 'notebook/tables/typed-values/TypedValueResolution';
import {StringTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {assertNever} from 'util/Types';

export type StoichiometryTableCircularColumnGroup =
  | StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE
  | StructuredTableColumnGroups.CHEMISTRY_MOLS
  | StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN;

export type StoichiometryCalculationCellDependencies = [
  error: TypedValue | null,
  isLimitingReactant: TypedValue | null,
  // Limiting Reactant Columns
  limitingReactantEquivalence: TypedValue | null,
  limitingReactantMols: TypedValue | null,
  limitingReactantMolsUnit: TypedValue | null,
  // Component Columns
  equivalence: TypedValue | null,
  moleculePurity: TypedValue | null,
  molecularWeight: TypedValue | null,
  mols: TypedValue | null,
  molUnit: TypedValue | null,
  amount: TypedValue | null,
  amountUnit: TypedValue | null,
  concentration: TypedValue | null,
  concentrationUnit: TypedValue | null,
];

export const StoichiometryColumnGroupLimitingReactantCellDependencies: Array<StructuredTableColumnGroups> = [
  StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE,
  StructuredTableColumnGroups.CHEMISTRY_MOLS,
  StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT,
];

export const StoichiometryColumnGroupCellDependencies: Array<StructuredTableColumnGroups> = [
  StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE,
  StructuredTableColumnGroups.CHEMISTRY_MOLECULE_PURITY,
  StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT,
  StructuredTableColumnGroups.CHEMISTRY_MOLS,
  StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT,
  StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN,
  StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT,
  StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION,
  StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION_UNIT,
];

function cellDependenciesToStoichiometryCalculationVariableValues(
  cellDependencies: StoichiometryCalculationCellDependencies
): StoichiometryCalculationVariableValues {
  const [
    _error,
    _isLimitingReactant,
    limitingReactantEquivalence,
    limitingReactantMols,
    limitingReactantMolsUnit,
    equivalence,
    moleculePurity,
    molecularWeight,
    mols,
    molUnit,
    amount,
    amountUnitTypedValue,
    concentration,
    concentrationUnitTypedValue,
  ] = cellDependencies;

  const limitingReactantMolConversion =
    getStandardChemistryUnitConversionFactorFromSymbol(limitingReactantMolsUnit, DEFAULT_MOL_UNIT_SYMBOL) ??
    undefined;
  const molConversion =
    getStandardChemistryUnitConversionFactorFromSymbol(molUnit, DEFAULT_MOL_UNIT_SYMBOL) ?? undefined;
  const moleculePurityPercentage = coerceToPrimitiveNumber(moleculePurity);

  const values: StoichiometryCalculationVariableValues = {
    [StoichiometryVariable.LIMITING_REACTANT_EQUIVALENCE]:
      coerceToPrimitiveNumber(limitingReactantEquivalence) ?? undefined,
    [StoichiometryVariable.LIMITING_REACTANT_MOLS]:
      coerceToPrimitiveNumber(limitingReactantMols) ?? undefined,
    [StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION]: limitingReactantMolConversion,
    // convert percentage to decimal, only if i's in a valid range
    [StoichiometryVariable.MOLECULE_PURITY]:
      moleculePurityPercentage && moleculePurityPercentage > 0 && moleculePurityPercentage <= 100
        ? moleculePurityPercentage / 100
        : undefined,
    [StoichiometryVariable.EQUIVALENCE]: coerceToPrimitiveNumber(equivalence) ?? undefined,
    [StoichiometryVariable.MOLS]: coerceToPrimitiveNumber(mols) ?? undefined,
    [StoichiometryVariable.MOL_UNIT_CONVERSION]: molConversion,
    [StoichiometryVariable.MOLECULAR_WEIGHT]: coerceToPrimitiveNumber(molecularWeight) ?? undefined,
  };

  const amountUnit = getUnitFromTypedValue(amountUnitTypedValue);
  if (amountUnit) {
    if (isVolumeUnit(amountUnit)) {
      values[StoichiometryVariable.VOLUME] = coerceToPrimitiveNumber(amount) ?? undefined;
      values[StoichiometryVariable.VOLUME_UNIT_CONVERSION] =
        getStandardChemistryUnitConversionFactorFromSymbol(
          amountUnitTypedValue,
          DEFAULT_VOLUME_UNIT_SYMBOL
        ) ?? undefined;
    } else if (isMassUnit(amountUnit)) {
      values[StoichiometryVariable.MASS] = coerceToPrimitiveNumber(amount) ?? undefined;
      values[StoichiometryVariable.MASS_UNIT_CONVERSION] =
        getStandardChemistryUnitConversionFactorFromSymbol(amountUnitTypedValue, DEFAULT_MASS_UNIT_SYMBOL) ??
        undefined;
    }
  }

  const concentrationUnit = getUnitFromTypedValue(concentrationUnitTypedValue);
  if (concentrationUnit) {
    if (isMolarityUnit(concentrationUnit)) {
      values[StoichiometryVariable.MOLARITY] = coerceToPrimitiveNumber(concentration) ?? undefined;
      values[StoichiometryVariable.MOLARITY_UNIT_CONVERSION] =
        getStandardChemistryUnitConversionFactorFromSymbol(
          concentrationUnitTypedValue,
          DEFAULT_MOLARITY_UNIT_SYMBOL
        ) ?? undefined;
    } else if (isDensityUnit(concentrationUnit)) {
      values[StoichiometryVariable.DENSITY] = coerceToPrimitiveNumber(concentration) ?? undefined;
      values[StoichiometryVariable.DENSITY_UNIT_CONVERSION] =
        getStandardChemistryUnitConversionFactorFromSymbol(
          concentrationUnitTypedValue,
          DEFAULT_DENSITY_UNIT_SYMBOL
        ) ?? undefined;
    }
  }

  return values;
}

export default function makeStoichiometryCalculationCustomEval(
  solveForColumn: StoichiometryTableCircularColumnGroup
): CustomEval<{
  tableMetadata: StoichiometryStructuredTableMetadata;
  rowMetadata: StoichiometryStructuredTableRowMetadata;
}> {
  function stoichiometryCustomEval(
    typedValue: TypedValue,
    columnDefinition: ColumnDefinition,
    cellDependencies: Array<TypedValue | null>,
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ): CustomEvalResult {
    if (cellDependencies.length <= 1 || !isTypedValueEmpty(typedValue)) {
      const errorValue = cellDependencies[0];
      return {
        val: typedValue,
        error: errorValue ? {type: 'ERROR', message: (errorValue as StringTypedValue).rawValue} : null,
        locked: false,
      };
    }
    const isLimitingReactant = coerceToPrimitiveBoolean(cellDependencies[1]) ?? false;
    // The limiting reactant equivalence is locked to 1
    if (isLimitingReactant && solveForColumn === StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE) {
      return {
        val: createFloatTypedValue(1),
        locked: true,
        error: null,
      };
    }

    const stoichiometryCalculationVariableValues = cellDependenciesToStoichiometryCalculationVariableValues(
      cellDependencies as StoichiometryCalculationCellDependencies
    );
    const solveForVariable = getSolveForVariableFromColumn(solveForColumn, {
      unitType: stoichiometryCalculationVariableValues.VOLUME_UNIT_CONVERSION != null ? 'volume' : 'mass',
    });
    const solverResult = StoichiometrySolver.solve(solveForVariable, stoichiometryCalculationVariableValues);
    // If we are missing any values, don't return an empty value. List out missing dependencies
    // in the error message.
    // eslint-disable-next-line benchlint/no-in-operator
    if ('missingDeps' in solverResult) {
      return {
        val: createFloatTypedValue(null),
        error: {type: 'ERROR', message: `Missing dependencies: ${[...solverResult.missingDeps].join(', ')}`},
        locked: false,
      };
    }
    // Otherwise, return the calculated value and lock the cell.
    const {solution} = solverResult;
    return {
      val: createFloatTypedValue(solution),
      error: null,
      locked: true,
    };
  }
  return stoichiometryCustomEval;
}

type StoichiometryAmountUnitType = 'mass' | 'volume';

function getSolveForVariableFromColumn(
  columnGroup: StoichiometryTableCircularColumnGroup,
  options: {unitType: StoichiometryAmountUnitType}
): StoichiometryVariable {
  switch (columnGroup) {
    case StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE:
      return StoichiometryVariable.EQUIVALENCE;
    case StructuredTableColumnGroups.CHEMISTRY_MOLS:
      return StoichiometryVariable.MOLS;
    case StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN:
      switch (options.unitType) {
        case 'mass':
          return StoichiometryVariable.MASS;
        case 'volume':
          return StoichiometryVariable.VOLUME;
        default:
          assertNever(options.unitType);
          break;
      }
      break;
    default:
      assertNever(columnGroup);
      break;
  }
  throw new Error(`Cannot figure out variable to solve for from column group: ${columnGroup}`);
}
